import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Play, BookOpen, CheckCircle, Clock, Heart } from 'lucide-react';
import { getCourseTypeLabel } from '../../lib/utils';
import { toast } from 'sonner';

export default function RecordedCourses() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playerDialog, setPlayerDialog] = useState({ open: false, course: null, videoIndex: 0 });
  const [showTrialCTA, setShowTrialCTA] = useState(false);

  const isFree = user?.learner_type === 'free' || !user?.learner_type;

  useEffect(() => {
    api.get('/api/courses').then(res => setCourses(res.data.courses || [])).finally(() => setLoading(false));
  }, []);

  const handleWatchVideo = async (course, videoIndex) => {
    setPlayerDialog({ open: true, course, videoIndex });
    // Mark as watching
    try {
      await api.post(`/api/courses/${course.id}/mark-watched?video_index=${videoIndex}&watch_percentage=100`);
      // Show trial CTA for free users after watching
      if (isFree && course.access_level === 'free') {
        setShowTrialCTA(true);
      }
      // Refresh courses
      const res = await api.get('/api/courses');
      setCourses(res.data.courses || []);
    } catch { }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Recorded Courses</h1>
        <p className="text-muted-foreground mt-1">Watch at your own pace</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{[1,2].map(i => <Card key={i} className="rounded-2xl animate-pulse"><CardContent className="h-40 pt-6" /></Card>)}</div>
      ) : courses.length === 0 ? (
        <Card className="rounded-2xl"><CardContent className="pt-6 text-center py-12">
          <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No recorded courses available yet</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map(c => (
            <Card key={c.id} className="rounded-2xl hover:shadow-md transition-shadow duration-200">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-semibold">{c.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.description}</p>
                  </div>
                  <Badge variant="secondary">{getCourseTypeLabel(c.course_type)}</Badge>
                </div>
                {c.trimester && <Badge variant="outline" className="mb-3">Trimester {c.trimester}</Badge>}
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>{c.completion_pct || 0}% complete</span>
                    <span>{(c.progress || c.completed_videos || []).length}/{c.total_videos || 0} videos</span>
                  </div>
                  <Progress value={c.completion_pct || 0} className="h-2" />
                </div>
                <div className="space-y-1">
                  {(c.videos || []).map((v, i) => {
                    const completed = (c.progress || c.completed_videos || []).includes(i);
                    return (
                      <button key={i} onClick={() => handleWatchVideo(c, i)}
                        className={`flex items-center gap-2 w-full p-2 rounded-lg text-sm text-left transition-colors duration-200 ${completed ? 'bg-emerald-50 text-emerald-800' : 'hover:bg-muted/40'}`}
                        data-testid={`video-${c.id}-${i}`}>
                        {completed ? <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" /> : <Play className="h-4 w-4 text-primary shrink-0" />}
                        <span className="flex-1 truncate">{v.title || `Video ${i + 1}`}</span>
                        {v.duration_minutes && <span className="text-xs text-muted-foreground">{v.duration_minutes}m</span>}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Video Player Dialog */}
      <Dialog open={playerDialog.open} onOpenChange={(o) => setPlayerDialog({ open: o, course: null, videoIndex: 0 })}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{playerDialog.course?.videos?.[playerDialog.videoIndex]?.title || 'Video'}</DialogTitle>
          </DialogHeader>
          {playerDialog.course?.videos?.[playerDialog.videoIndex]?.url ? (
            <>
              <div className="aspect-video rounded-xl overflow-hidden bg-black" onContextMenu={e => e.preventDefault()}>
                <iframe
                  src={playerDialog.course.videos[playerDialog.videoIndex].url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                  title="Video Player"
                />
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-1">Video downloads are disabled. Content is for personal use only.</p>
              {showTrialCTA && isFree && (
                <div className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between" data-testid="trial-cta-after-video">
                  <div className="flex items-center gap-2">
                    <Heart className="h-4 w-4 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-900">Enjoyed the video?</p>
                      <p className="text-xs text-emerald-700">Book a live class with our expert instructors</p>
                    </div>
                  </div>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 shrink-0" onClick={() => window.location.href = '/learner/dashboard'} data-testid="book-trial-from-video">
                    Book a trial class
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="aspect-video rounded-xl bg-muted flex items-center justify-center">
              <p className="text-muted-foreground">Video URL not available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
